import React, { useState } from 'react';
import { useClientLogs } from '@/hooks/supabase/useClientLogs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Copy } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const LogLevelBadge = ({ level }: { level: string | null }) => {
  if (!level) return null;
  const variant = {
    error: 'destructive',
    warn: 'secondary',
    info: 'default',
    debug: 'outline',
    log: 'outline',
  }[level] || 'default';

  return <Badge variant={variant as any}>{level}</Badge>;
};

const LogViewer = () => {
  const { logs, loading, error, hasMore, levelFilter, setLevelFilter, loadMore, refresh } = useClientLogs();

  const renderMeta = (meta: any) => {
    if (!meta || Object.keys(meta).length === 0) return <span className="text-muted-foreground">N/A</span>;
    const metaString = JSON.stringify(meta, null, 2);
    return (
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="item-1" className="border-none">
          <AccordionTrigger>View JSON</AccordionTrigger>
          <AccordionContent>
            <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto max-h-60">{metaString}</pre>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Email address copied to clipboard.",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Client Logs</CardTitle>
        <CardDescription>View logs sent from client applications.</CardDescription>
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-2">
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warn">Warn</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
                <SelectItem value="log">Log</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={refresh} disabled={loading}>Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-destructive">Error loading logs: {error.message}</p>}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Timestamp</TableHead>
                <TableHead className="w-[100px]">Level</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="w-[200px]">Meta</TableHead>
                <TableHead className="w-[250px]">User</TableHead>
                <TableHead className="w-[250px]">Session ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && logs.length === 0 ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell title={log.created_at ? new Date(log.created_at).toLocaleString() : 'No date'}>
                    {log.created_at ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true }) : 'N/A'}
                  </TableCell>
                  <TableCell><LogLevelBadge level={log.level} /></TableCell>
                  <TableCell><p className="max-w-xs truncate" title={log.message || ''}>{log.message}</p></TableCell>
                  <TableCell>{renderMeta(log.meta)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-pointer underline decoration-dotted">
                            {log.user_full_name || log.user_id || 'N/A'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="p-2">
                          <div className="flex flex-col text-sm">
                            <p><strong>User ID:</strong> <span className="font-mono">{log.user_id || 'N/A'}</span></p>
                            <p><strong>Full Name:</strong> {log.user_full_name || 'N/A'}</p>
                            <p className="flex items-center">
                              <strong>Email:</strong> <span className="font-mono mr-2">{log.user_email || 'N/A'}</span>
                              {log.user_email && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => copyToClipboard(log.user_email!)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              )}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{log.session_id || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {hasMore && (
          <div className="mt-4 text-center">
            <Button onClick={loadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
        {!loading && logs.length === 0 && (
          <p className="text-center text-muted-foreground mt-4">No logs found for this level.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default LogViewer;